import React from 'react';
import { clientRoutes, websiteDomain } from '@/ApiPaths';

/** Fictional desktop resolution the monitor graphic represents (scale comparison only). */
export const EMBED_PREVIEW_REFERENCE_MONITOR_W = 1920;
export const EMBED_PREVIEW_REFERENCE_MONITOR_H = 1080;

/** Query flags appended to /embed/:id — see ProductViewer embedQueryFlags */
export type EmbedIframeCopyOptions = {
  annotations: boolean;
  ar: boolean;
  playback: boolean;
  animations: boolean;
  reset: boolean;
  help: boolean;
  appearance: boolean;
};

export const EMBED_IFRAME_COPY_DEFAULTS: EmbedIframeCopyOptions = {
  annotations: true,
  ar: true,
  playback: true,
  animations: true,
  reset: true,
  help: true,
  appearance: true,
};

export const IFRAME_EMBED_PRESETS = [
  { value: 'blog-post', label: 'Blog Post (600x400)', width: '600px', height: '400px' },
  { value: 'sidebar', label: 'Sidebar (300x400)', width: '300px', height: '400px' },
  { value: 'hero-section', label: 'Hero Section (Full Width)', width: '100%', height: '600px' },
  { value: 'popup', label: 'Popup (500x400)', width: '500px', height: '400px' },
  { value: 'medium', label: 'Medium (800x500)', width: '800px', height: '500px' },
  { value: 'large', label: 'Large (1200x800)', width: '1200px', height: '800px' },
] as const;

/** Clamp numeric iframe preview dimensions so the mock stays reasonable. */
export function clampIframePreviewDim(n: number): number {
  return Math.min(2400, Math.max(120, Math.round(n)));
}

/**
 * Maps width/height strings (px, %, vw, vh, bare number) to numeric px for preview scaling only.
 */
export function parseIframeDimensionForPreview(
  value: string | number | undefined,
  fallback: number,
  axis: 'w' | 'h'
): number {
  if (value === undefined || value === null) return fallback;
  const raw = typeof value === 'number' ? `${value}px` : String(value).trim().toLowerCase();
  if (!raw || raw === 'auto') return fallback;

  const pxMatch = raw.match(/^([\d.]+)\s*px$/);
  if (pxMatch) return clampIframePreviewDim(parseFloat(pxMatch[1]));

  const pctMatch = raw.match(/^([\d.]+)\s*%$/);
  if (pctMatch) {
    const base = axis === 'w' ? 960 : 640;
    return clampIframePreviewDim((base * parseFloat(pctMatch[1])) / 100);
  }

  const vwMatch = raw.match(/^([\d.]+)\s*vw$/);
  if (vwMatch) return clampIframePreviewDim((1200 * parseFloat(vwMatch[1])) / 100);

  const vhMatch = raw.match(/^([\d.]+)\s*vh$/);
  if (vhMatch) return clampIframePreviewDim((800 * parseFloat(vhMatch[1])) / 100);

  const bareMatch = raw.match(/^([\d.]+)$/);
  if (bareMatch) return clampIframePreviewDim(parseFloat(bareMatch[1]));

  return fallback;
}

export function buildEmbedIframeSrc(productId: string, options: EmbedIframeCopyOptions): string {
  const path = clientRoutes.embed.replace(':id', productId);
  const params = new URLSearchParams();
  if (!options.annotations) params.set('annotations', '0');
  if (!options.ar) params.set('ar', '0');
  if (!options.playback) params.set('playback', '0');
  if (!options.animations) params.set('animations', '0');
  if (!options.reset) params.set('reset', '0');
  if (!options.help) params.set('help', '0');
  if (!options.appearance) params.set('appearance', '0');
  const q = params.toString();
  return `${websiteDomain}${path}${q ? `?${q}` : ''}`;
}

export function extractProductIdFromEmbedIframeSrc(src: string): string | null {
  try {
    const url = new URL(src);
    const m = url.pathname.match(/\/embed\/([^/]+)\/?$/);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

export function createIframeProps(
  productId: string,
  width: string = '',
  height: string = '400px',
  embedOptions: EmbedIframeCopyOptions = EMBED_IFRAME_COPY_DEFAULTS
): React.IframeHTMLAttributes<HTMLIFrameElement> {
  return {
    width: width || 'auto',
    height: height || '400px',
    src: buildEmbedIframeSrc(productId, embedOptions),
    title: 'ROBOTOP 3D-Viewer',
    frameBorder: '0',
    referrerPolicy: 'strict-origin-when-cross-origin',
    allowFullScreen: true,
    allow: 'camera; xr-spatial-tracking; fullscreen',
    loading: 'lazy',
  };
}

export function createIframeString(props: React.IframeHTMLAttributes<HTMLIFrameElement>): string {
  const camelToKebab = (str: string): string => str.replace(/([A-Z])/g, '-$1').toLowerCase();

  const stringProps = Object.entries(props)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const kebabKey = camelToKebab(key);
      if (typeof value === 'boolean') {
        return value ? kebabKey : '';
      }
      if (key === 'style' && typeof value === 'object') {
        return '';
      }
      return `${kebabKey}="${value}"`;
    })
    .filter((attr) => attr !== '')
    .join('\n  ');

  return `<iframe
  ${stringProps}
></iframe>`;
}
